import { analytics, bootElapsedMs, classifyDevice } from './analytics/Analytics.js';
import { ClientApp } from './game/ClientApp.js';
import { CLOUD_SAVE_KEYS, CrazyGamesSdk } from './game/CrazyGamesSdk.js';

// V4.1 — full client-error visibility for the CrazyGames Basic Launch
// crash-rate KPI. The dashboard infers "crashed" from a stuck loading
// state or an unfinished session; the V2.5 build only `console.warn`'d
// uncaught errors so we had zero data on what actually broke. Now every
// uncaught error and unhandled rejection emits a `client_error` event
// into the analytics pipeline — the singleton emitter holds the queue
// until ClientApp wires the analytics URL, so even pre-URL boot crashes
// surface once the post-boot flush fires.
//
// Listeners are installed BEFORE the cloud-save hydration begins so any
// throw inside the hydrate path (cross-origin storage exception, a
// future SDK-shape change) is captured. Each emit is wrapped in
// try/catch — analytics infra itself crashing must never derail boot.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    try {
      const err = e.error;
      const msg =
        err instanceof Error
          ? `${err.name}: ${err.message}`
          : typeof e.message === 'string' && e.message.length > 0
            ? e.message
            : 'unknown error';
      const stack = err instanceof Error ? err.stack : undefined;
      analytics.emit({
        kind: 'client_error',
        message: msg.slice(0, 512),
        source: 'window',
        ...(stack !== undefined && stack.length > 0
          ? { stack: stack.slice(0, 4096) }
          : {}),
        ...(typeof e.filename === 'string' && e.filename.length > 0
          ? { file: e.filename.slice(0, 512) }
          : {}),
        ...(typeof e.lineno === 'number' && e.lineno > 0 ? { line: e.lineno } : {}),
      });
    } catch {
      // Analytics infrastructure failure — must never derail boot.
    }
    // eslint-disable-next-line no-console
    console.warn('[uncaught error]', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    try {
      const reason: unknown = e.reason;
      const msg =
        reason instanceof Error
          ? `${reason.name}: ${reason.message}`
          : typeof reason === 'string'
            ? reason
            : 'unknown rejection';
      const stack = reason instanceof Error ? reason.stack : undefined;
      analytics.emit({
        kind: 'client_error',
        message: msg.slice(0, 512),
        source: 'unhandled_rejection',
        ...(stack !== undefined && stack.length > 0
          ? { stack: stack.slice(0, 4096) }
          : {}),
      });
    } catch {
      // see above
    }
    // eslint-disable-next-line no-console
    console.warn('[unhandled rejection]', e.reason);
  });
}

// Fire session_started immediately so the dashboard sees a "session" row
// even if everything below fails. The visibility / pagehide / beforeunload
// hooks are installed inside `analytics.install()` (called by ClientApp
// after it wires the URL) — they ensure the queue flushes before the page
// goes away.
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
  analytics.emit({
    kind: 'session_started',
    device: classifyDevice(),
    touch:
      'ontouchstart' in window ||
      (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0),
    screenW: window.innerWidth,
    screenH: window.innerHeight,
  });
}

// V1.8 — relay the cloud-hydrate outcome into analytics. The SDK
// dispatches a CustomEvent rather than importing analytics directly so
// it stays decoupled from the analytics module (which is configured by
// ClientApp, later in the boot).
if (typeof window !== 'undefined') {
  window.addEventListener('signalclash:cloud-hydrate-status', (evt) => {
    try {
      const detail = (evt as CustomEvent).detail as {
        outcome: 'success' | 'sdk_unavailable' | 'ready_timeout' | 'fetch_timeout' | 'error';
        keysAttempted: number;
        keysHydrated: number;
        durationMs: number;
        errorMessage?: string;
      } | undefined;
      if (detail === undefined) return;
      analytics.emit({
        kind: 'cloud_hydrate_status',
        outcome: detail.outcome,
        keysAttempted: detail.keysAttempted,
        keysHydrated: detail.keysHydrated,
        durationMs: detail.durationMs,
        ...(detail.errorMessage !== undefined
          ? { errorMessage: detail.errorMessage.slice(0, 200) }
          : {}),
      });
    } catch {
      // Same containment as the other listeners — analytics failure
      // never derails the game.
    }
  });
}

// V1.8.4 — cloud-save bootstrap. Construct the SDK before ClientApp so we
// can pull saved state from the CrazyGames Data Module into localStorage
// BEFORE any save module (Profile, Settings, AudioBus, Telemetry, ...)
// reads its synchronous defaults. hydrateAndInstallCloudSave is bounded
// (1.5s for SDK ready + 2s for the per-key cloud fetches) so a slow or
// blocked portal CDN never stalls boot — worst case the game starts with
// localStorage as-is, exactly like before this code existed.
const sdk = new CrazyGamesSdk();
// Time the SDK ready handshake so a slow portal CDN shows up as a high
// sdk_ready elapsedMs on the dashboard rather than being silently absorbed
// into the overall load time.
void sdk
  .ready()
  .then(() => {
    analytics.emit({ kind: 'sdk_ready', elapsedMs: bootElapsedMs() });
  })
  .catch(() => {
    // ready() never rejects in practice (it resolves on timeout / failure),
    // but defend against a future SDK change.
  });
void sdk.hydrateAndInstallCloudSave(CLOUD_SAVE_KEYS).finally(() => {
  const app = new ClientApp(sdk);
  void app.start();
});

// V1.9 — boot watchdog. CrazyGamesTricks §10.6 ("treat stuck loading as a
// crash"): if main_menu_visible never fires within the watchdog window,
// the loading sequence is stuck somewhere upstream of MainMenuScene.create.
// We emit a stuck_loading event so the dashboard can correlate spike days
// in crash_rate with this signature instead of guessing.
//
// 15 s is a deliberate floor: average loading time on the dashboard is
// 2.7 s, top-performers do < 10 s; anything past 15 s is unambiguously
// broken (Fly cold start, SDK hang, atlas-generation OOM on iOS). We
// fire ONCE and never block the boot — it's a diagnostic, not a fallback.
if (typeof window !== 'undefined') {
  const BOOT_WATCHDOG_MS = 15_000;
  let menuVisibleFired = false;
  const onMenuVisible = (): void => {
    menuVisibleFired = true;
    window.removeEventListener('signalclash:main-menu-visible', onMenuVisible);
  };
  window.addEventListener('signalclash:main-menu-visible', onMenuVisible);
  setTimeout(() => {
    if (menuVisibleFired) return;
    try {
      analytics.emit({
        kind: 'stuck_loading',
        elapsedMs: bootElapsedMs(),
        // Phase hint helps the dashboard partition the failures. We
        // can't know which stage stalled from here (the SDK might be
        // ready but ClientApp.start may have thrown silently), so we
        // emit the bucket name we *expected* to have crossed by now.
        expectedPhase: 'main_menu_visible',
      });
    } catch {
      // Analytics failure must not derail anything else on the page.
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[boot watchdog] main menu not visible after ${BOOT_WATCHDOG_MS}ms — see analytics.stuck_loading`,
    );
  }, BOOT_WATCHDOG_MS);
}
